import {useEffect, useMemo, useRef, useState} from 'react';
import {Button, Icon, TextField} from '@ezcater/tapas';
import {faChevronRight, faMagnifyingGlass, faXmark} from '@fortawesome/pro-regular-svg-icons';
import useTranslation from 'next-translate/useTranslation';
import {twJoin} from 'tailwind-merge';

import EmptyStateIllustration from '@/assets/images/illustrations/empty-state-help-search.svg?react';
import useTracking from '@/hooks/useTracking';
import useWebchatEntryPoint from '@/hooks/useWebchatEntryPoint';
import {gtm} from '@/utils';
import {contactUsClickPayload} from '@/utils/googleAnalyticsEvents';
import {type HelpCenterArticle, type HelpCenterCollection} from '@/utils/helpCenter/types';
import trackChatStarted from '@/utils/trackChatStarted';
import ArticleLink from './ArticleLink';
import CategoryGrid from './CategoryGrid';
import {useHelpPanelSession} from './HelpPanelSession';
import VisitHelpCenterCta from './VisitHelpCenterCta';

type ChatTrigger = 'no-results' | 'load-error';

type HelpPanelProps = {
  experimentVariation: string;
  /** Lets the surrounding modal close itself, so chat is not left behind the overlay. */
  onChatStart?: () => void;
  showHeading?: boolean;
  layout?: 'panel' | 'page';
  renderCta?: boolean;
};

const HelpPanel: React.FC<HelpPanelProps> = ({
  experimentVariation,
  onChatStart,
  showHeading = true,
  layout = 'panel',
  renderCta = true,
}) => {
  const isPage = layout === 'page';
  const {t} = useTranslation('common');
  const {track, trackClick} = useTracking();
  const {openWebchat} = useWebchatEntryPoint({
    utmCampaign: isPage ? 'help-page-no-results' : 'help-modal-no-results',
  });
  const {
    data,
    loading,
    error,
    query,
    setQuery,
    debouncedQuery,
    clearSearch: resetSearch,
    isSearching,
    results,
    selected,
    setSelected,
  } = useHelpPanelSession();

  const bodyRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const hasRenderedOnce = useRef(false);

  useEffect(() => {
    if (!hasRenderedOnce.current) {
      hasRenderedOnce.current = true;
      return;
    }
    bodyRef.current?.focus();
  }, [selected]);

  const [contentOverflows, setContentOverflows] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const el = bodyRef.current;
    if (isPage || !el) return undefined;

    const update = () => setIsScrolled(el.scrollTop > 0);
    update();

    el.addEventListener('scroll', update, {passive: true});
    return () => el.removeEventListener('scroll', update);
  }, [isPage]);

  const selectedArticles = useMemo(
    () =>
      selected ? data.articles.filter(article => article.collectionIds.includes(selected.id)) : [],
    [selected, data.articles],
  );

  useEffect(() => {
    const el = bodyRef.current;
    if (isPage || !el) return undefined;

    const update = () => setContentOverflows(el.scrollHeight > el.clientHeight);
    update();

    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [isPage, loading, error, isSearching, results.length, selected, selectedArticles.length]);

  const clearSearch = () => {
    resetSearch();
    searchInputRef.current?.focus();
  };

  // The category grid, a collection, and search results all share this scroller; switching
  // between them should land on top rather than wherever the previous view left scrollTop.
  useEffect(() => {
    if (isPage) return;
    bodyRef.current?.scrollTo({top: 0});
  }, [isPage, loading, error, isSearching, debouncedQuery, selected]);

  const startChat = (trigger: ChatTrigger) => {
    trackChatStarted();
    gtm.dataLayer(contactUsClickPayload({contactType: 'chat'}));
    track('clicked-contact-us-link', {
      page: window.location.pathname,
      misc_json: JSON.stringify({
        method: 'chat',
        variant: isPage ? 'page' : 'modal',
        experiment_variation: experimentVariation,
        source: `help-panel-${trigger}`,
      }),
    });
    openWebchat();
    onChatStart?.();
  };

  const trackCategorySelected = (collection: HelpCenterCollection) => {
    trackClick('help-category-card', {
      misc_json: JSON.stringify({
        collection_id: collection.id,
        collection_name: collection.name,
        article_count: collection.articleCount,
      }),
    });
    setSelected(collection);
  };

  const resolveCollectionName = (article: HelpCenterArticle) =>
    data.collections.find(collection => article.collectionIds.includes(collection.id))?.name;

  const trackArticleSelected = (
    article: HelpCenterArticle,
    source: 'category-grid' | 'search-results',
    collectionName?: string,
  ) => {
    trackClick('help-article-link', {
      misc_json: JSON.stringify({
        article_id: article.id,
        article_title: article.title,
        collection_name: collectionName ?? null,
        source,
      }),
    });
  };

  // Skeletons and the result count both change without moving focus, so the state has to be spoken.
  const liveStatus = () => {
    if (loading) return t('components.Loading');
    if (error) return t('components.ContactUs.help.loadError');
    if (isSearching) return t('components.ContactUs.help.resultCount', {count: results.length});
    return '';
  };

  const renderEmptyState = (body: string, testId: string, trigger: ChatTrigger) => (
    <div
      className={twJoin(
        'flex flex-col items-center gap-4 rounded-2xl border border-peppercorn-100 p-8 text-center',
        !isPage && 'min-h-0 flex-1 justify-center',
      )}
      data-testid={testId}
    >
      <EmptyStateIllustration aria-hidden width={160} height={120} className="shrink-0" />
      <p className="text-base text-peppercorn-800">{body}</p>
      <Button
        variant="outlined"
        onClick={() => startChat(trigger)}
        data-testid="help-panel-chat-cta"
      >
        {t('components.ContactUs.help.chatWithUs')}
      </Button>
    </div>
  );

  const renderBody = () => {
    if (loading) {
      return (
        <div className="flex flex-col gap-4" data-testid="help-panel-loading">
          {Array.from({length: 4}).map((_, index) => (
            <div key={index} className="h-24 animate-pulse rounded-2xl bg-peppercorn-50" />
          ))}
        </div>
      );
    }

    if (error) {
      return renderEmptyState(
        t('components.ContactUs.help.loadError'),
        'help-panel-error',
        'load-error',
      );
    }

    if (isSearching) {
      if (results.length === 0) {
        return (
          <div
            className={isPage ? 'flex flex-col gap-3' : 'flex h-full flex-col gap-3'}
            data-testid="help-panel-no-results"
          >
            <p className="text-sm text-peppercorn-500">
              {t('components.ContactUs.help.noResultsTitle')}
            </p>
            {renderEmptyState(
              t('components.ContactUs.help.noResultsBody'),
              'help-panel-no-results-card',
              'no-results',
            )}
          </div>
        );
      }

      return (
        <div className="flex flex-col gap-3" data-testid="help-panel-results">
          <p className="text-sm text-peppercorn-500">
            {t('components.ContactUs.help.resultCount', {count: results.length})}
          </p>
          {results.map(article => (
            <ArticleLink
              key={article.id}
              article={article}
              query={debouncedQuery}
              variant="card"
              onSelect={selectedArticle =>
                trackArticleSelected(
                  selectedArticle,
                  'search-results',
                  resolveCollectionName(selectedArticle),
                )
              }
            />
          ))}
        </div>
      );
    }

    if (selected) {
      return (
        <div className="flex flex-col gap-2" data-testid="help-panel-collection">
          <nav aria-label={t('components.ContactUs.help.collectionBreadcrumb')}>
            <ol className="flex items-center gap-2 text-xl">
              <li>
                <button
                  type="button"
                  onClick={() => setSelected(null)}
                  className="font-bold text-peppercorn-800 underline underline-offset-2 hover:text-peppercorn-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-700 focus-visible:ring-offset-2"
                >
                  {t('components.ContactUs.help.allCollections')}
                </button>
              </li>
              <li aria-hidden className="text-peppercorn-400">
                <Icon icon={faChevronRight} size="xsmall" />
              </li>
              <li aria-current="page" className="font-bold text-peppercorn-600">
                {selected.name}
              </li>
            </ol>
          </nav>
          <p className="text-sm text-peppercorn-500">
            {t('components.ContactUs.help.articleCount', {count: selectedArticles.length})}
          </p>
          <div className="flex flex-col">
            {selectedArticles.map(article => (
              <ArticleLink
                key={article.id}
                article={article}
                onSelect={selectedArticle =>
                  trackArticleSelected(selectedArticle, 'category-grid', selected.name)
                }
              />
            ))}
          </div>
        </div>
      );
    }

    return <CategoryGrid collections={data.collections} onSelect={trackCategorySelected} />;
  };

  return (
    <div
      className={isPage ? 'flex flex-col gap-6' : 'relative flex h-full flex-col gap-6'}
      data-testid="help-panel"
    >
      {showHeading && (
        <h2 className="text-xl font-bold text-peppercorn-800">
          {t('components.ContactUs.help.title')}
        </h2>
      )}

      {!error && (
        <TextField.Root>
          <TextField.Slot>
            <Icon className="text-peppercorn-400" icon={faMagnifyingGlass} size="xsmall" />
          </TextField.Slot>
          <TextField.Input
            ref={searchInputRef}
            aria-label={t('components.ContactUs.help.searchPlaceholder')}
            placeholder={t('components.ContactUs.help.searchPlaceholder')}
            value={query}
            onChange={event => setQuery(event.target.value)}
          />
          {query && (
            <TextField.Slot>
              <button
                type="button"
                onClick={clearSearch}
                aria-label={t('components.ContactUs.help.clearSearch')}
              >
                <Icon className="text-peppercorn-400" icon={faXmark} size="xsmall" />
              </button>
            </TextField.Slot>
          )}
        </TextField.Root>
      )}

      <div aria-live="polite" role="status" className="sr-only">
        {liveStatus()}
      </div>

      {/* Padded so the last row clears the pinned button, which content scrolls behind. */}
      <div
        ref={bodyRef}
        tabIndex={-1}
        className={
          isPage
            ? 'flex flex-col focus:outline-none'
            : '-mx-4 -mt-1 flex min-h-0 flex-1 flex-col overflow-y-auto px-4 pb-20 pt-1 focus:outline-none tablet:-mx-8 tablet:px-8'
        }
        data-testid="help-panel-body"
      >
        {!isPage && (
          <div
            aria-hidden
            className={twJoin(
              'pointer-events-none sticky -top-1 z-10 -mx-4 -mb-2 h-2 shrink-0 tablet:-mx-8',
              isScrolled && 'bg-gradient-to-b from-black/[0.12] to-transparent',
            )}
          />
        )}
        {renderBody()}
      </div>

      {renderCta &&
        (isPage ? (
          <VisitHelpCenterCta />
        ) : (
          <div
            className={twJoin(
              'absolute inset-x-0 bottom-0 -mx-4 bg-white px-4 pt-4 tablet:-mx-8 tablet:px-8',
              // Cast upward only: a zero-offset shadow bleeds past the panel edges onto the modal.
              contentOverflows && 'shadow-[0_-4px_6px_-4px_rgba(0,0,0,0.15)]',
            )}
          >
            <VisitHelpCenterCta />
          </div>
        ))}
    </div>
  );
};

export default HelpPanel;
